import React, { useState, useContext, useEffect } from "react";
import { Modal, ModalBody } from "@windmill/react-ui";
import { SidebarContext } from "context/SidebarContext";
import { IoIosClose } from "react-icons/io";
import useDriverSubmit from "hooks/useDriverSubmit";
import CommonCloseBtn from "components/CloseButton/CommonCloseButton";

const ViewModal = ({ id }) => {
  const { isViewModalOpen, closeModal } = useContext(SidebarContext);

  const [, setActiveTab] = useState("1");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);

  const { result } = useDriverSubmit(id);

  /* ---------------- LANGUAGE NORMALIZER ---------------- */
  const normalizeLanguages = (langs_id, language_known) => {
    // Case 1: array of objects
    if (
      Array.isArray(langs_id) &&
      langs_id.length > 0 &&
      typeof langs_id[0] === "object"
    ) {
      return langs_id.map((l) => ({
        id: String(l.id),
        name: l.lang_driver,
      }));
    }

    // Case 2: array of strings
    if (Array.isArray(language_known) && language_known.length > 0) {
      return language_known.map((name, index) => ({
        id: String(index),
        name,
      }));
    }

    // Case 3: array of ids only
    if (Array.isArray(langs_id) && langs_id.length > 0) {
      return langs_id.map((id) => ({
        id: String(id),
        name: `Language ${id}`,
      }));
    }

    return [];
  };

  /* ---------------- SET LANGUAGES ON DATA LOAD ---------------- */
  useEffect(() => {
    if (result) {
      const langs = normalizeLanguages(
        result?.langs_id,
        result?.language_known
      );
      setSelectedLanguages(langs);
    }
  }, [result]);

  /* ---------------- IMAGE HANDLERS ---------------- */
  const openZoomModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeZoomModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  const renderImage = (image) => (
    <img
      className="inline-flex border rounded-md border-gray-100 dark:border-gray-600 w-24 max-h-24 p-2 cursor-pointer"
      src={process.env.REACT_APP_API_IMAGE_URL + image}
      alt="Preview"
    />
  );

  return (
    <>
      <Modal
        className="relative bg-white dark:bg-gray-700 dark:text-white w-1/2 rounded-lg"
        isOpen={isViewModalOpen}
        onClose={() => {
          setActiveTab("1");
          closeModal();
        }}
      >
        <ModalBody className="overflow-y-auto" style={{ maxHeight: "70vh" }}>
              <CommonCloseBtn
               onClose={closeModal}
              />
          <h2 className="text-center font-bold text-lg mb-4">
            Driver Details
          </h2>

          <dl className="border rounded-lg border-gray-300 dark:border-gray-500 text-sm">

            {/* Vendor */}
            <Row label="Vendor Name" value={result?.vendor_name} />

            {/* Driver */}
            <Row label="Driver Name" value={result?.driver_name} />

            {/* Driver Image */}
            <Row
              label="Driver Image"
              value={
                Array.isArray(result?.driver_img)
                  ? result.driver_img.map(renderImage)
                  : result?.driver_img && renderImage(result.driver_img)
              }
              onClick={() => openZoomModal(result?.driver_img)}
            />

            {/* Mobile */}
            <Row label="Driver Mobile" value={result?.driver_contact_no} />

            {/* License */}
            <Row
              label="Driver Licence"
              value={
                Array.isArray(result?.license_img)
                  ? result.license_img.map(renderImage)
                  : result?.license_img && renderImage(result.license_img)
              }
              onClick={() => openZoomModal(result?.license_img)}
            />

            {/* Aadhaar */}
            <Row label="Driver Aadhaar" value={result?.aadhaar_no} />

            {/* ✅ Language Known */}
            <div className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-3 sm:gap-4">
              <dt className="font-medium text-gray-900 dark:text-gray-200">
                Language Known
              </dt>
              <dd className="text-gray-700 dark:text-gray-200 sm:col-span-2 pl-20">
                {selectedLanguages.length > 0 ? (
                  selectedLanguages.map((lang, i) => (
                    <span key={lang.id}>
                      {lang.name}
                      {i !== selectedLanguages.length - 1 && ", "}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400">No language info</span>
                )}
              </dd>
            </div>

            {/* Rating */}
            <Row label="Rating" value={result?.driver_rating} />

            {/* Status */}
            <Row
              label="Active Status"
              value={result?.status === 0 ? "Active" : "Deactive"}
            />

            <Row
              label="Block Status"
              value={result?.is_blocked === 1 ? "Blocked" : "Unblocked"}
            />

            {/* Created */}
            <Row
              label="Created Date"
              value={
                result?.added_date
                  ? new Date(result.added_date).toLocaleString()
                  : ""
              }
            />

            {/* Updated */}
            <Row
              label="Updated Date"
              value={
                result?.updated_date
                  ? new Date(result.updated_date).toLocaleString()
                  : ""
              }
            />
          </dl>
        </ModalBody>
      </Modal>

      {/* IMAGE ZOOM */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-600 p-4 rounded-lg relative">
            <button
              onClick={closeZoomModal}
              className="absolute top-2 right-2"
            >
              <IoIosClose size={28} />
            </button>
            <img
              src={process.env.REACT_APP_API_IMAGE_URL + selectedImage}
              alt="Zoom"
              className="max-w-full rounded"
            />
          </div>
        </div>
      )}
    </>
  );
};

/* ---------------- REUSABLE ROW ---------------- */
const Row = ({ label, value, onClick }) => (
  <div
    className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-3 sm:gap-4"
    onClick={onClick}
  >
    <dt className="font-medium text-gray-900 dark:text-gray-200">
      {label}
    </dt>
    <dd className="text-gray-700 dark:text-gray-200 sm:col-span-2 pl-20">
      {value || "-"}
    </dd>
  </div>
);

export default React.memo(ViewModal);
